import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { UserCheck, Plus, Edit, Trash2, Mail, Phone, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '@/components/Layout';
import AddPartnerModal from '@/components/AddPartnerModal';
import EditPartnerModal from '@/components/EditPartnerModal';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 10;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const PartnersPage = ({ user, onLogout }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', id: '', name: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/users/partners`, getAuthHeaders());
      setPartners(response.data);
    } catch (error) {
      toast.error('Erro ao carregar parceiros');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartner = async (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    const partnerName = partner?.name || partner?.email || 'Parceiro';
    
    setDeleteTarget({
      type: 'single',
      id: partnerId,
      name: partnerName
    });
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteSelected = async () => {
    if (selectedPartners.length === 0) return;
    
    setDeleteTarget({
      type: 'batch',
      id: selectedPartners,
      name: `${selectedPartners.length} parceiro(s)`
    });
    setShowDeleteConfirmModal(true);
  };

  const executeDelete = async () => {
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === 'single') {
        await axios.delete(`${API}/users/partners/${deleteTarget.id}`, getAuthHeaders());
        toast.success('Parceiro excluído com sucesso');
        setSelectedPartners(prev => prev.filter(id => id !== deleteTarget.id));
      } else if (deleteTarget.type === 'batch') {
        let deleted = 0;
        for (const partnerId of deleteTarget.id) {
          try {
            await axios.delete(`${API}/users/partners/${partnerId}`, getAuthHeaders());
            deleted++;
          } catch (error) {
            // Continue with others
          }
        }
        toast.success(`${deleted} parceiro(s) excluído(s) com sucesso`);
        setSelectedPartners([]);
      }
      fetchPartners();
      setShowDeleteConfirmModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir parceiro');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSelectPartner = (partnerId) => {
    setSelectedPartners(prev => 
      prev.includes(partnerId) 
        ? prev.filter(id => id !== partnerId) 
        : [...prev, partnerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPartners.length === paginatedPartners.length) {
      setSelectedPartners([]);
    } else {
      setSelectedPartners(paginatedPartners.map(p => p.id));
    }
  };

  const totalPages = Math.ceil(partners.length / ITEMS_PER_PAGE);
  const paginatedPartners = partners.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <UserCheck className="h-8 w-8 text-primary" />
              Parceiros
            </h1>
            <p className="text-muted-foreground mt-1">
              {partners.length} parceiro(s) cadastrado(s)
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Parceiro
          </Button>
        </div>

        {/* Partners List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Carregando...</div>
        ) : partners.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Nenhum parceiro cadastrado
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Select All Bar */}
            <div className="mb-4 p-3 bg-muted/50 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="select-all-partners"
                    checked={selectedPartners.length === paginatedPartners.length && paginatedPartners.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-partners-checkbox"
                  />
                  <label htmlFor="select-all-partners" className="text-sm font-medium cursor-pointer">
                    Selecionar Todos
                  </label>
                </div>
                {selectedPartners.length > 0 && (
                  <Badge variant="secondary">{selectedPartners.length} selecionado(s)</Badge>
                )}
              </div>
              {selectedPartners.length > 0 && (
                <Button variant="destructive" size="sm" onClick={handleDeleteSelected} data-testid="delete-selected-partners-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Selecionados
                </Button>
              )}
            </div>

            <div className="space-y-4">
              {paginatedPartners.map((partner, index) => (
                <Card key={partner.id} className={`hover:shadow-md transition-shadow ${selectedPartners.includes(partner.id) ? 'ring-2 ring-primary' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          checked={selectedPartners.includes(partner.id)}
                          onCheckedChange={() => handleSelectPartner(partner.id)}
                          className="mt-1"
                          data-testid={`partner-checkbox-${partner.id}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-primary/10 rounded-full">
                              <Building2 className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{partner.company_name || 'Autônomo'}</h3>
                              <p className="text-sm text-muted-foreground">{partner.name}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {partner.email}
                            </div>
                            {partner.telefone && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                {partner.telefone}
                              </div>
                            )}
                          </div>
                          <div className="mt-3">
                            <Badge variant="secondary">
                              {partner.client_count || 0} cliente(s)
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-8 md:ml-0">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => { setEditingPartner(partner); setShowEditModal(true); }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeletePartner(partner.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Modals */}
        {showAddModal && (
          <AddPartnerModal
            onClose={() => setShowAddModal(false)}
            onSuccess={() => { setShowAddModal(false); fetchPartners(); }}
          />
        )}
        {showEditModal && editingPartner && (
          <EditPartnerModal
            partner={editingPartner}
            onClose={() => { setShowEditModal(false); setEditingPartner(null); }}
            onSuccess={() => { setShowEditModal(false); setEditingPartner(null); fetchPartners(); }}
          />
        )}
        <DeleteConfirmationModal
          isOpen={showDeleteConfirmModal}
          onClose={() => setShowDeleteConfirmModal(false)}
          onConfirm={executeDelete}
          title="Excluir Parceiro(s)"
          description="Esta ação é irreversível. Os clientes associados a este parceiro NÃO serão excluídos, mas ficarão sem parceiro responsável."
          itemName={deleteTarget.name}
          itemType="Parceiro"
          loading={deleteLoading}
        />
      </div>
    </Layout>
  );
};

export default PartnersPage;
